import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MuiButton,
  Chip,
  Switch,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Typography, PageHeader } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import DataTable from '@/components/common/DataTable';
import type { TableColumn, TableAction } from '@/components/common/DataTable/types';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import { UserFormDrawer } from '../../components';
import { usersService } from '@/superadmin/services/usersService';
import { toast } from 'react-hot-toast';
import { useSuperadminAuth } from '@/superadmin/contexts/SuperadminAuthContext';

export interface UserRow extends Record<string, unknown> {
  id: string;
  name: string;
  email: string;
  phone_number: string;
  is_active: boolean;
  updated_at: string;
}

const initialUsers: UserRow[] = [];

const UsersPage: React.FC = () => {
  const [rows, setRows] = useState<UserRow[]>(initialUsers);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<UserRow | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [searchKey, setSearchKey] = useState<string>('name');
  const [searchValue, setSearchValue] = useState<string>('');
  const [appliedFilters, setAppliedFilters] = useState<{ name?: string; email?: string }>({});
  const { user } = useSuperadminAuth();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const resp = await usersService.list(
          currentPage,
          pageSize,
          appliedFilters.name,
          appliedFilters.email
        );
        const apiUsers = resp?.results || [];
        // console.log('API Users:', apiUsers);
        const mapped: UserRow[] = apiUsers.map(u => ({
          id: u.uuid,
          name: u.name,
          email: u.email,
          phone_number: u.phone_number,
          is_active: u.is_active,
          updated_at: u.updated_at,
        }));
        setRows(mapped);
        setTotalCount(resp?.count || 0);
      } catch (e) {
        console.warn('Failed to load users:', e);
        toast.error('Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [currentPage, pageSize, appliedFilters]);

  // Breadcrumbs
  const breadcrumbConfig = getBreadcrumbConfig('/users');
  const breadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
  });

  const finalBreadcrumbs =
    breadcrumbs.length > 0
      ? breadcrumbs
      : [
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Users', active: true },
        ];

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Filter handlers
  const handleSearch = () => {
    const newFilters = {
      [searchKey]: searchValue.trim() || undefined,
    };

    // Don’t update if no change
    if (appliedFilters?.[searchKey] === newFilters[searchKey]) {
      fetchUsers();
      return;
    }

    setAppliedFilters(prev => ({
      ...prev,
      ...newFilters,
    }));

    setCurrentPage(1);
  };
  const handleClearSearch = () => {
    setAppliedFilters({});

    setSearchValue('');
    setSearchKey('name');

    setCurrentPage(1);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  if (!user) return <div>No Access</div>;

  // Toggle active status
  const handleToggleActive = async (userId: string, currentStatus: boolean) => {
    try {
      setToggling(userId);
      await usersService.toggleActive(userId, !currentStatus);

      // Update local state
      setRows(prevRows =>
        prevRows.map(row => (row.id === userId ? { ...row, is_active: !currentStatus } : row))
      );

      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.warn('Failed to toggle user status:', error);
      toast.error('Failed to update user status');
    } finally {
      setToggling(null);
    }
  };

  // Columns
  const columns: TableColumn<UserRow>[] = [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 200,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2" fontWeight="medium">
          {String(params.value)}
        </Typography>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 250,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2" color="primary">
          {String(params.value)}
        </Typography>
      ),
    },
    {
      field: 'phone_number',
      headerName: 'Phone',
      minWidth: 150,
      sortable: false,
      renderCell: params => (
        <Typography variant="body2">{String(params.value) || 'N/A'}</Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      minWidth: 120,
      sortable: true,
      renderCell: params => (
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={params.value ? 'Active' : 'Inactive'}
            color={params.value ? 'success' : 'default'}
            size="small"
            variant="outlined"
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(params.value)}
                onChange={() => handleToggleActive(params.row.id, Boolean(params.value))}
                disabled={toggling === params.row.id}
                size="small"
              />
            }
            label=""
            sx={{ margin: 0 }}
          />
        </Box>
      ),
    },
  ];

  const [spinning, setSpinning] = useState(false);

  const handleRefresh = async () => {
    setSpinning(true);

    try {
      await handleSearch(); // 🔥 hits every click & syncs spinner
    } catch (error) {
      console.error(error);
    } finally {
      setSpinning(false);
    }
  };
  //Check Permission Access
  const hasPermission = (permission: string) => {
    if (user.is_superuser) return true; // superuser has everything
    return user.permission_list?.includes(permission);
  };

  // Header actions
  const headerActions: PageHeaderAction[] = [
    ...(hasPermission('ADMIN_USER_ADD_USER')
      ? [
          {
            label: 'New User',
            icon: <AddIcon sx={{ fontSize: 16 }} />,
            onClick: () => {
              setEditing(null);
              setDrawerOpen(true);
            },
            variant: 'contained',
            size: 'medium',
          },
        ]
      : []),
  ];

  const actions: TableAction<UserRow>[] = [
    hasPermission('ADMIN_USER_EDIT_USER')
      ? {
          label: 'Edit',
          icon: <EditIcon />,
          color: 'primary' as const,
          onClick: ({ row }) => {
            setEditing(row);
            setDrawerOpen(true);
          },
        }
      : null,

    hasPermission('ADMIN_USER_DELETE_USER')
      ? {
          label: 'Delete',
          icon: <DeleteIcon />,
          color: 'error' as const,
          danger: true,
          onClick: ({ row }) => {
            setToDelete(row);
            setConfirmOpen(true);
          },
        }
      : null,
  ].filter(Boolean) as TableAction<UserRow>[];

const handleSubmitUser = async (payload: {
  id?: string;
  name: string;
  email: string;
  phone_number: string;
  password?: string;
  is_active: boolean;
}) => {
  try {
    setLoading(true);

    if (editing) {
      // Update existing user
      const updateResp = await usersService.update(editing.id, {
        name: payload.name,
        email: payload.email,
        phone_number: payload.phone_number,
        is_active: payload.is_active,
      });

      // Check API response
      if (updateResp.success === false) {
        toast.error(updateResp.message || 'Failed to update user');
        return;
      }

      toast.success('User updated');
    } else {
      // Create new user
      if (!payload.password) {
        toast.error('Password is required');
        setLoading(false);
        return;
      }

      const createResp = await usersService.create({
        name: payload.name,
        email: payload.email,
        phone_number: payload.phone_number,
        password: payload.password,
        is_active: payload.is_active,
      });

      // Check API response
      if (createResp.success === false) {
        toast.error(createResp.message || 'Failed to create user');
        return;
      }

      toast.success('User created');
    }

    // Refresh the users list
    const resp = await usersService.list(
      currentPage,
      pageSize,
      appliedFilters.name,
      appliedFilters.email
    );

    const mapped: UserRow[] = (resp?.results || []).map(u => ({
      id: u.uuid,
      name: u.name,
      email: u.email,
      phone_number: u.phone_number,
      is_active: u.is_active,
      updated_at: u.updated_at,
    }));

    setRows(mapped);
    setTotalCount(resp?.count || 0);
    setDrawerOpen(false);
    setEditing(null);
  } catch (e: any) {
    // Handle network or unexpected errors
    let apiMessage = 'Failed to save user';

    if (e?.response?.data?.message) {
      apiMessage = e.response.data.message;
    } else if (e?.message) {
      apiMessage = e.message;
    }

    toast.error(apiMessage);
    console.warn('Save user failed:', e);
  } finally {
    setLoading(false);
  }
};


  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle={`Manage system users (${totalCount} ${totalCount === 1 ? 'user' : 'users'})`}
        breadcrumbs={finalBreadcrumbs}
        actions={headerActions}
        showDivider={false}
      />
      {/* Filter Controls */}
      <Box
        mb={3}
        display="flex"
        gap={2}
        alignItems="center"
        flexWrap="wrap"
        justifyContent="space-between"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      >
        <Box display="flex" gap={2} alignItems="center">
          <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: 200,
              background: 'white',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          >
            <Select value={searchKey} onChange={e => setSearchKey(e.target.value)}>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </Select>
          </FormControl>

          <TextField
            variant="outlined"
            type="search"
            placeholder={`Enter ${searchKey.replace(/_/g, ' ')}`}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              maxWidth: 300,
              '& .MuiOutlinedInput-root': {
                background: 'white',
              },
            }}
          />
          {/* Clear Filters Button */}
          <Button variant="text" size="small" onClick={handleClearSearch}>
            Clear Filters
          </Button>
        </Box>

        <Button onClick={handleRefresh} disabled={spinning} variant="text" size="small">
          <RefreshIcon
            sx={{
              animation: spinning ? 'spin 1s linear infinite' : 'none',
            }}
          />
        </Button>
      </Box>
      {hasPermission('ADMIN_USER_LIST_VIEW') ? (
        <DataTable
          data={rows}
          columns={columns}
          actions={actions}
          keyField="id"
          title="Users"
          showTitle={false}
          loading={loading}
          pagination={true}
          rowsPerPageOptions={[5, 10, 15, 25, 50]}
          defaultRowsPerPage={15}
          page={currentPage - 1}
          rowsPerPage={pageSize}
          totalCount={totalCount}
          onPageChange={page => handlePageChange(page + 1)}
          onRowsPerPageChange={handlePageSizeChange}
          sortable={true}
          stickyHeader={true}
          maxHeight={600}
          striped={true}
          emptyState={
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <Typography variant="body1">No users found.</Typography>
            </Box>
          }
        />
      ) : (
        <Typography variant="body1" color="textSecondary">
          You do not have permission to view users.
        </Typography>
      )}
      <UserFormDrawer
        open={drawerOpen}
        initialValues={
          editing
            ? {
                id: editing.id,
                name: editing.name,
                email: editing.email,
                phone_number: editing.phone_number,
                is_active: editing.is_active,
              }
            : null
        }
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmitUser}
      />
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{toDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmOpen(false)}>Cancel</MuiButton>
          <MuiButton
            color="error"
            onClick={async () => {
              if (!toDelete) return;
              try {
                setLoading(true);
                await usersService.delete(toDelete.id);
                const resp = await usersService.list(
                  currentPage,
                  pageSize,
                  appliedFilters.name,
                  appliedFilters.email
                );
                const mapped: UserRow[] = (resp?.results || []).map(u => ({
                  id: u.uuid,
                  name: u.name,
                  email: u.email,
                  phone_number: u.phone_number,
                  is_active: u.is_active,
                  updated_at: u.updated_at,
                }));
                setRows(mapped);
                setTotalCount(resp?.count || 0);
                toast.success('User deleted');
              } catch (e) {
                toast.error('Failed to delete user');
                console.warn('Delete user failed:', e);
              } finally {
                setLoading(false);
                setConfirmOpen(false);
                setToDelete(null);
              }
            }}
          >
            Delete
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
